import {
  computed,
  inject,
  Injectable,
  Injector,
  signal,
  Signal,
  untracked,
  WritableSignal,
} from "@angular/core";
import {
  ArrayFieldDef,
  CertificateDefinition,
  FieldDef,
  GroupFieldDef,
} from "./models/certificate.schema";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
} from "@angular/forms";
import { distinctUntilChanged, map, startWith } from "rxjs";
import {
  ArraySection,
  FieldWithControl,
  FormSection,
  GroupSection,
} from "./models/form-section";
import { toSignal } from "@angular/core/rxjs-interop";
import { parseValidators } from "./helpers/validator-parser";
import { CertificateService } from "./certificate.service";

@Injectable()
export class CertificateState {
  private fb = inject(FormBuilder);
  private injector = inject(Injector);
  private certs = inject(CertificateService);

  /**
   * Initialize or re-initialize state for a specific certificate type/id.
   * Will no-op if already initialized for the same key, so child navigations
   * (overview <-> section) do not wipe in-progress edits.
   */
  initialize(type: string, id?: string | undefined) {
    const currentType = this.type();
    const currentId = this.id();

    // Only reset if type or id changed (navigating to different certificate)
    if (currentType !== type || currentId !== id) {
      console.log(`CertificateState: resetting for ${type}/${id || 'new'}`);
      this.reset();
    }

    // Set new params
    this.type.set(type);
    if (id) this.id.set(id);
  }

  /**
   * Completely resets the state - useful for navigation between different certificates
   */
  reset() {
    // Reset all signals to free up memory and break any circular references
    this.type.set(null);
    this.id.set(null);
    this.definition.set(null);
    this.certificateDefinitionVersion.set(null);
    this.certificateData.set(null);
    this.loadedSections.set({});
  }

  type: WritableSignal<string | null> = signal(null);
  id: WritableSignal<string | null> = signal(null);
  definition: WritableSignal<CertificateDefinition | null> = signal(null);
  certificateDefinitionVersion: WritableSignal<number | null> = signal(null);
  certificateData: WritableSignal<Record<string, unknown> | null> = signal(null);
  loadedSections: WritableSignal<Record<string, boolean>> = signal({});
  form: Signal<FormGroup> = computed(() => {
    const def = this.definition();
    if (!def) {
      return this.fb.group({});
    }

    const controls = def.sections.reduce((acc, f) => {
      // Recursively build a FormGroup or FormArray based on field definitions
      return { ...acc, [f.id]: this.buildControl(f) };
    }, {});

    return this.fb.group(controls);
  });
  formValid: Signal<boolean> = computed(() => {
    const form = this.form(); // Track the form dependency
    return untracked(() => toSignal(
      form.statusChanges.pipe(
        startWith(null),
        map(() => form.valid),
      ),
      { initialValue: form.valid, injector: this.injector }
    ))();
  });
  sections: Signal<Record<string, FormSection>> = computed(() => {
    const def = this.definition();
    if (!def || !def.sections) return {};

    return def.sections.reduce((acc: Record<string, FormSection>, section) => {
      const sectionId = section.id || "default";

      if (section.type === "group") {
        acc[sectionId] = this.createGroupSection(section);
      }

      if (section.type === "array") {
        acc[sectionId] = this.createArraySection(section);
      }

      return acc;
    }, {});
  });

  // Whether every section has finished loading (for gating generate)
  allSectionsLoaded: Signal<boolean> = computed(() => {
    if (!this.id()) return true; // new certs have no data to load

    const keys = Object.keys(this.sections());
    const loaded = this.loadedSections();
    return keys.every((k) => loaded[k]);
  });

  addArrayControlToSection({ formArray, fieldDefs }: ArraySection) {
    const arrayItemFields: any = {};
    fieldDefs.forEach((child) => {
      arrayItemFields[child.id] = this.buildControl(child);
    });
    formArray.push(this.fb.group(arrayItemFields));
  }

  private createGroupSection({
    label,
    id,
    fields: sectionFields,
    meta,
  }: GroupFieldDef): GroupSection {
    const formGroup = this.form().get(id) as FormGroup;
    const value = formGroup?.value;
    const fields = (sectionFields || [])
      .filter((field) => !!field.label)
      .map((field) => {
        // Ensure the section FormGroup has a child control for this field. This is
        // important when the field is itself a group — Angular templates that use
        // [formGroupName] rely on the parent FormGroup containing a FormGroup
        // with the matching key.
        const parentGroup = formGroup as FormGroup;
        let control: AbstractControl | null = this.form().get(
          [id, field.id].join("."),
        ) as AbstractControl | null;
        if (!control) {
          // Build the control for the field and add it to the parent section group
          const built = this.buildControl(field);
          parentGroup.addControl(field.id, built as any);
          control = parentGroup.get(field.id) as AbstractControl;
        }

        const path = [id, field.id].join(".");
        const base: FieldWithControl = {
          ...field,
          control: untracked(() => toSignal(
            (control as any).statusChanges.pipe(
              map(() => control),
              startWith(control),
            ),
            { injector: this.injector }
          )) as Signal<any>,
        } as FieldWithControl;

        // If this field is itself a group, expose its child fields with controls
        if ((field as GroupFieldDef).type === "group") {
          const childDefs = (field as GroupFieldDef).fields || [];
          base.childFields = childDefs.map((child) => {
            const childPath = [id, field.id, child.id].join(".");
            let childControl = this.form().get(
              childPath as any,
            ) as AbstractControl | null;
            // If a child control doesn't exist (shouldn't normally happen), build it
            if (!childControl) {
              const parentChildGroup = parentGroup.get(field.id) as FormGroup;
              const builtChild = this.buildControl(child);
              parentChildGroup.addControl(child.id, builtChild as any);
              childControl = parentChildGroup.get(child.id) as AbstractControl;
            }

            return {
              ...child,
              control: untracked(() => toSignal(
                childControl!.statusChanges.pipe(
                  map(() => childControl),
                  startWith(childControl),
                ),
                { injector: this.injector }
              )) as Signal<any>,
            } as FieldWithControl;
          });
        }

        return base as FieldWithControl;
      });

    const groupStatus = untracked(() => toSignal(
      formGroup.statusChanges.pipe(startWith(formGroup.status)),
      { initialValue: formGroup.status, injector: this.injector }
    ));

    return {
      id,
      label,
      formGroup,
      value,
      fieldControls: fields,
      status: groupStatus,
      type: "group",
      meta,
    };
  }

  private createFieldControls(formGroup: FormGroup, arrayFields: FieldDef[]): FieldWithControl[] {
    return (arrayFields || []).map((field) => {
      const control = formGroup.get(field.id)!;
      return {
        ...field,
        control: untracked(() => toSignal(
          control.statusChanges.pipe(
            map(() => control),
            startWith(control),
          ),
          { injector: this.injector }
        )) as Signal<any>,
      };
    });
  }

  private createArraySection({
    label,
    id,
    itemLabel,
    fields: arrayFields,
    itemSummary,
  }: ArrayFieldDef): ArraySection {
    const formArray = this.form().get(id) as FormArray;
    const initialItems = formArray.controls.map((formGroup, index) => {
      const fields = this.createFieldControls(formGroup as FormGroup, arrayFields);

      return {
        formGroup,
        fields,
      };
    });

    const observable = formArray.valueChanges.pipe(
      distinctUntilChanged((prev: any[], curr: any[]) => prev.length === curr.length),
      map(() => formArray),
      map((array) => {
        return array.controls.map((formGroup, index) => {
          const fields = this.createFieldControls(formGroup as FormGroup, arrayFields);

          return {
            formGroup,
            fields,
          };
        });
      })
    );

    const items = untracked(() => toSignal(observable, { initialValue: initialItems, injector: this.injector }));
    const value = formArray?.value;
    const sectionStatus = untracked(() => toSignal(
      formArray.statusChanges.pipe(
        map(() => formArray.status),
        startWith(formArray.status),
      ),
      { initialValue: formArray.status, injector: this.injector }
    ));

    return {
      id,
      label,
      formArray,
      itemLabel,
      fieldDefs: arrayFields,
      value,
      items,
      status: sectionStatus,
      type: "array",
      itemSummary,
    };
  }

  private buildControl(field: FieldDef): FormGroup | FormArray | FormControl {
    if (field.type === "group") {
      const groupFields: any = {};
      (field.fields || []).forEach((child) => {
        groupFields[child.id] = this.buildControl(child);
      });
      return this.fb.group(groupFields);
    } else if (field.type === "array") {
      const arrayItemFields: any = {};
      (field.fields || []).forEach((child) => {
        arrayItemFields[child.id] = this.buildControl(child);
      });
      return this.fb.array([this.fb.group(arrayItemFields)], {
        validators: parseValidators(field.validators),
      });
    } else {
      return this.fb.nonNullable.control(null, {
        validators: parseValidators(field.validators),
      });
    }
  }

  /**
   * Prefills the form with existing certificate data.
   * Handles arrays and groups recursively.
   */
  prefillCertificate(data: Record<string, unknown>) {
    const form = this.form();
    if (!form) return;
    // Patch the form with the provided data
    form.patchValue(data);
    // For arrays, ensure the FormArray has enough controls
    Object.entries(data).forEach(([key, value]) => {
      const control = form.get(key);
      if (control instanceof FormArray && Array.isArray(value)) {
        // Add controls if needed
        while (control.length < value.length) {
          // Find section definition for this array
          const section = this.sections()[key] as ArraySection;
          if (section && section.type === "array") {
            this.addArrayControlToSection(section);
          }
        }
        // Patch each item
        value.forEach((item, idx) => {
          const group = control.at(idx);
          if (group instanceof FormGroup) {
            group.patchValue(item as Record<string, unknown>);
          }
        });
      }
    });
  }

  /**
   * Loads the certificate data lazily if not already loaded, and prefills the form.
   */
  async loadCertificateData() {
    const type = this.type();
    const id = this.id();
    if (!type || !id || id === "new" || this.certificateData()) return;

    const result = await this.certs.getCertificate<Record<string, unknown>>(type, id);
    if (result) {
      this.certificateData.set(result.data);
      this.prefillCertificate(result.data);
    }
  }

  /** Load a single section's data via RPC and patch into the form */
  async loadSection(sectionKey: string) {
    const type = this.type();
    const id = this.id();
    if (!type || !id || id === "new") return;
    console.log(`Loading section ${sectionKey} for ${type}/${id}`);
    try {
      const section = await this.certs.getCertificateSection<unknown>(type, id, sectionKey);
      // Update certificateData incrementally
      this.certificateData.update((prev) => ({ ...(prev ?? {}), [sectionKey]: section }));
      // Prefill the form from the updated data (handles groups, arrays, and adding controls)
      const data = this.certificateData();
      if (data) {
        this.prefillCertificate(data);
      }
    } finally {
      // Mark as loaded regardless of success so UI doesn't spin forever
      this.loadedSections.update((s) => ({ ...s, [sectionKey]: true }));
    }
  }
}
